import React, { useCallback, useState } from "react";
import { Tabs, Button, Form, Empty, message, Input, Popconfirm } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import AssessmentTable from "@/components/AssessmentTable";
import DynamicForm from "@/components/DynamicForm";
// import { useSendInvitation } from "../hooks/useSendInvitation";
import { deleteEvaluationInvitation } from "@/apis/supplier";
import type { FormInstance } from "antd";
import type { AssessmentItemConfig } from "@/components/AssessmentTable";
import styles from "./style.module.less";

// ==================== 类型定义 ====================
/**
 * 后端返回的评估节点结构
 */
interface BackendEvaluationNode {
  id: string;
  name: string;
  nodeType: "STAGE" | "DIMENSION" | "ITEM";
  parentId?: string;
  levelList?: Array<{
    checked: boolean;
    description: string;
    levelName: string;
    remark: string;
  }>;
  childList?: BackendEvaluationNode[];
}

// ==================== 常量配置 ====================

/**
 * 评估邀请状态映射
 * evaluationStatus: 评估状态
 *   - undefined/null: 待发送邀请
 *   - 1: 评估中
 *   - 2: 已评估
 */
const getEvaluationInvitationStatusMap = (
  t: any,
): Record<number, { color: string; text: string }> => ({
  1: { color: "processing", text: t("评估中") },
  2: { color: "success", text: t("已评估") },
});

const TAB_ITEMS_CONFIG = [
  {
    key: "my-assessment",
    label: "我的评估",
  },
  {
    key: "assessment-invite",
    label: "评估邀请",
  },
];

const EVALUATION_CATEGORIES = [
  { label: "前置筛选评估", value: "pre_screening" },
  { label: "质量维度", value: "quality" },
  { label: "体系维度", value: "system" },
  { label: "技术能力维度", value: "technical" },
  { label: "成本竞争力维度", value: "cost" },
  { label: "交付能力维度", value: "delivery" },
  { label: "创新能力维度", value: "innovation" },
];

// 默认评估人选项（备用）
const DEFAULT_ASSESSOR_OPTIONS = [
  { label: "陈怡然", value: "chenYiran" },
  { label: "张三", value: "zhangSan" },
  { label: "李四", value: "liSi" },
  { label: "王五", value: "wangWu" },
];

// ==================== 渲染函数 ====================

/**
 * 渲染评估邀请状态
 * 根据 evaluationStatus 判断显示状态徽章或发送邀请按钮
 * @param evaluationStatus - 评估状态 (1: 评估中, 2: 已评估, undefined/null: 待发送)
 * @param isSending - 是否正在发送邀请
 * @param onSendClick - 点击发送按钮的回调
 * @param t - 国际化函数
 *
 * 逻辑流程：
 * 1. 如果 evaluationStatus 存在（1或2），直接显示对应状态徽章
 * 2. 如果 evaluationStatus 为 undefined/null，显示"发送邀请"按钮
 */
const renderInvitationStatus = (
  evaluationStatus: number | undefined | null,
  isSending: boolean,
  onSendClick: () => void,
  t: any,
) => {
  const EVALUATION_INVITATION_STATUS_MAP = getEvaluationInvitationStatusMap(t);

  // 如果 evaluationStatus 存在，显示状态徽章
  if (evaluationStatus !== undefined && evaluationStatus !== null) {
    const statusInfo = EVALUATION_INVITATION_STATUS_MAP[evaluationStatus];

    if (statusInfo) {
      // 根据状态动态生成颜色
      // statusInfo.color 可能的值: "processing" | "success"
      const statusColors: Record<
        string,
        { bg: string; text: string; dot: string }
      > = {
        processing: {
          bg: "#E6F7FF", // 蓝色背景：评估中
          text: "#333333",
          dot: "#1890FF", // 蓝色圆点
        },
        success: {
          bg: "#D9FFDB", // 绿色背景：已评估
          text: "#333333",
          dot: "#00B42A", // 绿色圆点
        },
      };
      const colors = statusColors[statusInfo.color] || statusColors.success;

      return (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "88px",
            height: "32px",
            gap: "4px",
            borderRadius: "3px",
            fontSize: "14px",
            fontWeight: 500,
            backgroundColor: colors.bg,
            color: colors.text,
          }}
        >
          <span
            style={{
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              backgroundColor: colors.dot,
              flexShrink: 0,
            }}
          />
          {statusInfo.text}
        </span>
      );
    }
  }

  // 如果没有 evaluationStatus，显示发送邀请按钮
  // return (
  //   <Button
  //     type="primary"
  //     loading={isSending}
  //     disabled={isSending}
  //     onClick={onSendClick}
  //   >
  //     {isSending ? "发送中..." : "发送邀请"}
  //   </Button>
  // );
};

// ==================== 评估邀请表单配置 ====================
const getAssessmentInviteFormItems = (
  onDelete?: (index: number) => void,
  formRef?: React.MutableRefObject<FormInstance | null> | FormInstance,
  evaluationCategoryOptions: typeof EVALUATION_CATEGORIES = EVALUATION_CATEGORIES,
  assessorOptions: typeof DEFAULT_ASSESSOR_OPTIONS = DEFAULT_ASSESSOR_OPTIONS,
  assessorLoading: boolean = false,
  onAssessorSearch?: (value: string) => void,
  onSendInvitation?: (params: any, index: number) => Promise<any>,
  sendingInvitationIndex?: number,
  onOpenPersonnelModal?: (fieldName: number) => void,
  onDeleteInvitation?: (id: string | number) => Promise<void>,
  editId?: string | number, // 编辑接口的ID
  t?: (key: string) => string, // 国际化函数
) => [
  {
    name: "assessmentInviteItems",
    col: { span: 24 },
    list: [
      {
        label: t?.("评估阶段") || "评估阶段",
        name: "assessmentPhase",
        col: { span: 8 },
        rules: [
          {
            required: true,
            message: t?.("请选择评估阶段") || "请选择评估阶段",
          },
        ],
        widget: {
          widgetType: "Select",
          placeholder: t?.("请选择") || "请选择",
          // 过滤掉无效的空选项，确保只显示有效的选项
          options:
            evaluationCategoryOptions?.filter(
              (option: any) => option.value && option.label,
            ) || [],
          mode: "multiple", // ✅ 改为多选模式
          notFoundContent: null, // 没有匹配项时不显示任何内容
        },
      },
      {
        label: t?.("评估人") || "评估人",
        name: "assessmentPersonName",
        col: { span: 8 },
        rules: [{ message: t?.("请选择") || "请选择" }],
        custom: {
          render: (fieldName?: number) => {
            let form: FormInstance | null = null;
            if (formRef && "getFieldValue" in formRef) {
              // 直接是 FormInstance
              form = formRef as FormInstance;
            } else if (formRef && "current" in formRef) {
              // 是 ref 对象
              form = (formRef as any).current;
            }

            const allAssessmentItems = form?.getFieldValue(
              "assessmentInviteItems",
            );
            const currentRowData = allAssessmentItems?.[fieldName as number];
            // 优先显示人名，如果没有则显示 id
            const displayText =
              currentRowData?.assessmentPersonName ||
              currentRowData?.assessmentPerson ||
              "";

            return (
              <Input
                readOnly
                placeholder={t?.("请选择评估人") || "请选择评估人"}
                value={displayText}
                onClick={() => {
                  if (onOpenPersonnelModal) {
                    onOpenPersonnelModal(fieldName as number);
                  }
                }}
                style={{ cursor: "pointer" }}
              />
            );
          },
        },
      },
      {
        name: "statusOrAction",
        col: { span: 2 },
        custom: {
          render: (
            fieldName?: number,
            remove?: (index: number) => void,
            fieldValue?: any,
          ) => {
            // 从表单中获取所有评估邀请项
            let form: FormInstance | null = null;
            if (formRef && "getFieldValue" in formRef) {
              // 直接是 FormInstance
              form = formRef as FormInstance;
            } else if (formRef && "current" in formRef) {
              // 是 ref 对象
              form = (formRef as any).current;
            }

            const allAssessmentItems = form?.getFieldValue(
              "assessmentInviteItems",
            );
            const currentRowData = allAssessmentItems?.[fieldName as number];

            // 获取评估状态 (来自后端的 srmSupplierAccessEvaluationInvitationVOS 中的 evaluationStatus)
            // 当 evaluationStatus 存在时，会显示状态徽章（评估中/已评估）
            // 当 evaluationStatus 为 undefined/null 时，显示发送邀请按钮
            const evaluationStatus = currentRowData?.evaluationStatus;
            const isSending = sendingInvitationIndex === fieldName;

            return (
              <div className={styles.actionButtonContainer}>
                {renderInvitationStatus(
                  evaluationStatus,
                  isSending,
                  async () => {
                    if (!onSendInvitation) return;

                    // 构建发送邀请的参数
                    const params = {
                      evaluationTemplateId: currentRowData?.assessmentPhase,
                      purchaserId: currentRowData?.assessmentPerson,
                      evaluationStatus: 1,
                      id: editId, // 编辑接口的ID
                    };
                    await onSendInvitation(params, fieldName as number);
                  },
                  t,
                )}
              </div>
            );
          },
        },
      },
      {
        name: "deleteBtn",
        col: { span: 2 },
        custom: {
          render: (
            fieldName?: number,
            remove?: (index: number) => void,
            fieldValue?: any,
          ) => {
            // 获取邀请ID（如果存在的话）
            let form: FormInstance | null = null;
            if (formRef && "getFieldValue" in formRef) {
              form = formRef as FormInstance;
            } else if (formRef && "current" in formRef) {
              form = (formRef as any).current;
            }

            const allAssessmentItems = form?.getFieldValue(
              "assessmentInviteItems",
            );
            const currentRowData = allAssessmentItems?.[fieldName as number];
            const invitationId = currentRowData?.id; // 邀请ID

            const handleDelete = async () => {
              if (fieldName === undefined) return;

              try {
                // 如果有邀请ID，先调用删除接口
                if (invitationId && onDeleteInvitation) {
                  await onDeleteInvitation(invitationId);
                  message.success(t?.("删除成功") || "删除成功");
                }

                // 删除表单行
                if (remove) {
                  remove(fieldName);
                }
              } catch (error: any) {
                message.error(error?.message || t?.("删除失败") || "删除失败");
              }
            };

            return (
              <div className={styles.actionButtonContainer}>
                <Popconfirm
                  title={t?.("删除邀请") || "删除邀请"}
                  description={
                    t?.("确认要删除这条邀请吗") || "确认要删除这条邀请吗？"
                  }
                  onConfirm={handleDelete}
                  okText={t?.("确认") || "确认"}
                  cancelText={t?.("取消") || "取消"}
                >
                  <Button type="text" danger icon={<DeleteOutlined />}>
                    {t?.("删除") || "删除"}
                  </Button>
                </Popconfirm>
              </div>
            );
          },
        },
      },
    ],
  },
];

// ==================== 工具函数 ====================
/**
 * 将后端 ITEM 节点转换为 AssessmentItemConfig 格式
 * 直接使用 ITEM 的 levelList 作为评估等级
 */
const convertItemNodeToConfig = (
  itemNode: BackendEvaluationNode,
): AssessmentItemConfig => {
  return {
    key: itemNode.id,
    label: itemNode.name,
    list: [
      {
        id: 1,
        description: itemNode.name,
        level: "",
        levelList: (itemNode.levelList || []).map((level) => ({
          levelName: level.levelName,
          description: level.description,
          remark: level.remark || "",
        })),
      },
    ],
  };
};

// ==================== 自定义表格组件 ====================
/**
 * 评估表格容器
 * 直接遍历后端树形数据结构：STAGE > DIMENSION > ITEM
 * 每个 DIMENSION 对应一个 AssessmentTable 表格
 */
const AssessmentTableContainer = React.forwardRef<
  {
    getEvaluationData: () => BackendEvaluationNode[];
    getRemarksData: () => Record<string, Record<string, string>>;
    initializeSelectionFromData: (data: BackendEvaluationNode[]) => void;
  },
  { backendData?: BackendEvaluationNode[] }
>(({ backendData }, ref) => {
  const { t } = useTranslation("evaluationTab");
  // 维护选择状态的映射: key -> selectedLevel
  const [selectionMap, setSelectionMap] = React.useState<
    Record<string, string>
  >({});
  // 维护AssessmentTable组件的refs，以便收集remarks数据
  const tableRefsMap = React.useRef<Record<string, any>>({});

  // 当 backendData 改变时，清空选择状态
  React.useEffect(() => {
    setSelectionMap({});
    tableRefsMap.current = {};
  }, [backendData]);

  const handleRatingChange = (key: string, rating: string) => {
    // 更新选择状态
    setSelectionMap((prev) => ({
      ...prev,
      [key]: rating,
    }));
  };

  const getRemarksText = useCallback((key: string) => {
    // 这个方法已弃用，现在由 AssessmentTable 直接管理 remarks
    return null;
  }, []);

  // 暴露获取评估数据的方法
  React.useImperativeHandle(ref, () => ({
    getEvaluationData: () => {
      if (!backendData) return [];

      // 深拷贝数据，避免修改原始数据
      const clonedData = JSON.parse(JSON.stringify(backendData));

      // 先收集所有表格的 remarks 数据
      const allRemarksData: Record<string, string> = {};
      Object.entries(tableRefsMap.current).forEach(([tableKey, tableRef]) => {
        if (tableRef && tableRef.getRemarksValues) {
          const tableRemarks = tableRef.getRemarksValues();
          // 合并所有表格的remarks数据
          Object.assign(allRemarksData, tableRemarks);
        }
      });

      // 根据选择状态更新 checked 字段和 remark 字段
      const updateCheckedAndRemarks = (nodes: BackendEvaluationNode[]) => {
        nodes.forEach((node) => {
          if (node.levelList && node.levelList.length > 0) {
            const selectedLevel = selectionMap[node.id];
            if (selectedLevel) {
              // 从合并后的remarks数据中获取该节点对应的remarks值
              const nodeRemark = allRemarksData[node.id] || "";

              node.levelList.forEach((level) => {
                // 更新checked状态
                level.checked = level.levelName === selectedLevel;
                // 如果该级别被选中，则更新remark
                if (level.levelName === selectedLevel) {
                  level.remark = nodeRemark;
                }
              });
            }
          }

          // 递归处理子节点
          if (node.childList && node.childList.length > 0) {
            updateCheckedAndRemarks(node.childList);
          }
        });
      };

      updateCheckedAndRemarks(clonedData);
      return clonedData;
    },
    // 获取remarks数据的方法
    getRemarksData: () => {
      // 收集所有AssessmentTable的remarks数据
      const allRemarks: Record<string, Record<string, string>> = {};

      Object.entries(tableRefsMap.current).forEach(([tableKey, tableRef]) => {
        if (tableRef && tableRef.getRemarksValues) {
          allRemarks[tableKey] = tableRef.getRemarksValues();
        }
      });

      return allRemarks;
    },
    // 清空所有评估选择状态
    clearSelectionMap: () => {
      setSelectionMap({});
    },
    // 从已有数据初始化选择状态（用于编辑模式回显数据）
    initializeSelectionFromData: (data: BackendEvaluationNode[]) => {
      const newSelectionMap: Record<string, string> = {};
      const remarksMap: Record<string, string> = {};

      // 递归遍历数据树，收集已选中的等级和remarks
      const traverseData = (nodes: BackendEvaluationNode[]) => {
        nodes.forEach((node) => {
          if (node.levelList && node.levelList.length > 0) {
            // 查找被checked的level
            const checkedLevel = node.levelList.find((level) => level.checked);
            if (checkedLevel) {
              newSelectionMap[node.id] = checkedLevel.levelName;
              // 保存remarks信息（以nodeId为key，便于AssessmentTable查找）
              remarksMap[node.id] = checkedLevel.remark || "";
            }
          }

          // 递归处理子节点
          if (node.childList && node.childList.length > 0) {
            traverseData(node.childList);
          }
        });
      };

      traverseData(data);
      setSelectionMap(newSelectionMap);

      // 使用 setTimeout 确保 refs 已经被正确设置
      setTimeout(() => {
        Object.entries(tableRefsMap.current).forEach(([tableKey, tableRef]) => {
          if (tableRef && tableRef.initializeWithData) {
            tableRef.initializeWithData(newSelectionMap, remarksMap);
          }
        });
      }, 100);
    },
  }));

  // 如果没有数据，显示空状态
  if (!backendData || backendData.length === 0) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}
      >
        <Empty description={t("暂无数据")} />
      </div>
    );
  }

  // 遍历后端树形数据：STAGE > DIMENSION > ITEM
  return (
    <div>
      {backendData.map((stageNode) => {
        // 只处理 STAGE 类型的节点
        if (stageNode.nodeType !== "STAGE") return null;

        return (
          <div key={stageNode.id}>
            <div className={styles.sectionTitle}>{stageNode.name}</div>

            {/* 遍历 STAGE 下的所有 DIMENSION 节点 */}
            {stageNode.childList?.map((dimensionNode) => {
              // 只处理 DIMENSION 类型的节点
              if (dimensionNode.nodeType !== "DIMENSION") return null;

              const tableKey = `${stageNode.id}-${dimensionNode.id}`;

              // 提取该维度下的所有 ITEM 节点并转换为 AssessmentItemConfig
              const items =
                dimensionNode.childList
                  ?.filter((node) => node.nodeType === "ITEM")
                  .map((itemNode) => convertItemNodeToConfig(itemNode)) || [];

              // 如果该维度没有评估项，跳过渲染
              if (items.length === 0) return null;

              return (
                <AssessmentTable
                  key={tableKey}
                  ref={(ref) => {
                    // 保存table组件的ref，用于后续收集remarks数据
                    if (ref) {
                      tableRefsMap.current[tableKey] = ref;
                    }
                  }}
                  items={items}
                  title={dimensionNode.name}
                  onRatingChange={handleRatingChange}
                  getRemarksText={getRemarksText}
                  useBackendFormat={true}
                />
              );
            })}
          </div>
        );
      })}
    </div>
  );
});

AssessmentTableContainer.displayName = "AssessmentTableContainer";

// ==================== 模拟数据常量 ====================
const MOCK_ASSESSMENT_INVITE_DATA = [
  // 新增时默认一个空行，内容为空
  {
    assessmentPhase: [],
    assessmentPerson: "",
  },
];

// ==================== 类型定义 ====================
interface AssessmentInviteFormContainerProps {
  evaluationCategoryOptions?: typeof EVALUATION_CATEGORIES;
  editId?: string | number; // 编辑接口的ID
}

// ==================== 评估邀请表单容器（用于嵌套在主表单中） ====================
// 注意：这个组件被嵌套在主表单的 TabsField 中，所以不能有独立的 Form
// 必须使用 renderForm={false} 来避免 Form 嵌套警告
const AssessmentInviteFormContainer: React.FC<
  AssessmentInviteFormContainerProps
> = ({ evaluationCategoryOptions = EVALUATION_CATEGORIES, editId }) => {
  const { t } = useTranslation("evaluationTab");
  // 使用 Form.useFormInstance 获取父级表单实例
  const parentForm = Form.useFormInstance();
  const formRef = React.useRef<FormInstance>(null);
  const [sendingInvitationIndex] = useState<number | undefined>();
  const [personnelModalOpen, setPersonnelModalOpen] = useState(false);
  const [currentModalFieldIndex, setCurrentModalFieldIndex] = useState<
    number | undefined
  >();

  // 使用发送邀请Hook
  // const { handleSendInvitation } = useSendInvitation();

  const handleDeleteRow = (fieldName: number) => {
    // 使用父级表单实例
    const form = parentForm || formRef.current;
    if (form) {
      const assessmentInviteItems = form.getFieldValue("assessmentInviteItems");
      if (assessmentInviteItems && Array.isArray(assessmentInviteItems)) {
        const newItems = assessmentInviteItems.filter(
          (_: any, index: number) => index !== fieldName,
        );
        form.setFieldValue("assessmentInviteItems", newItems);
      }
    }
  };

  // 处理打开人员选择弹窗
  const handleOpenPersonnelModal = (fieldIndex: number) => {
    setCurrentModalFieldIndex(fieldIndex);
    setPersonnelModalOpen(true);
  };

  // 处理人员选择弹窗关闭
  const handleClosePersonnelModal = () => {
    setPersonnelModalOpen(false);
    setCurrentModalFieldIndex(undefined);
  };

  // 处理人员选择
  const handlePersonnelSelect = useCallback(
    (personnel: any) => {
      const form = parentForm || formRef.current;
      if (form && currentModalFieldIndex !== undefined) {
        const assessmentInviteItems = form.getFieldValue(
          "assessmentInviteItems",
        );
        if (assessmentInviteItems && Array.isArray(assessmentInviteItems)) {
          const updatedItems = [...assessmentInviteItems];
          updatedItems[currentModalFieldIndex] = {
            ...updatedItems[currentModalFieldIndex],
            assessmentPerson: personnel.userId,
            assessmentPersonName: personnel.nickName || personnel.userName,
          };
          // 使用 setFieldsValue 确保触发表单更新和 onValuesChange
          form.setFieldsValue({
            assessmentInviteItems: updatedItems,
          });
        }
      }
      handleClosePersonnelModal();
    },
    [parentForm, currentModalFieldIndex],
  );

  // 处理发送邀请
  // const handleSendInvitationClick = useCallback(
  //   async (params: any, index: number) => {
  //     setSendingInvitationIndex(index);
  //     try {
  //       // 获取完整的表单数据（包括申请人、供应商等信息）
  //       const form = parentForm || formRef.current;
  //       if (!form) {
  //         message.error("表单实例不可用");
  //         return;
  //       }
  //
  //       // 获取所有表单值
  //       const allFormValues = form.getFieldsValue();
  //
  //       // 构建发送邀请的完整参数（包含表单中的所有数据）
  //       const invitationParams = {
  //         ...allFormValues, // 包含表单中的所有数据
  //         ...params, // 邀请相关的参数（evaluationTemplateId, purchaserId）
  //       };
  //
  //       console.log("发送邀请的完整参数:", invitationParams);
  //       const result = await handleSendInvitation(invitationParams, evaluationCategoryOptions);
  //       console.log("result", result);
  //       // 根据返回结果设置 isSendInvite 参数
  //       const isSendInvite = result.code === 200;
  //       if (result.code === 200) {
  //         // 发送成功，更新表单中该行的状态
  //         const assessmentInviteItems = form.getFieldValue(
  //           "assessmentInviteItems",
  //         );
  //         if (assessmentInviteItems && Array.isArray(assessmentInviteItems)) {
  //           assessmentInviteItems[index] = {
  //             ...assessmentInviteItems[index],
  //             evaluationStatus: 1, // 发送邀请成功后，设置evaluationStatus为1
  //             assessmentStatus: false, // 已发送邀请，标记为未评估
  //             isSendInvite, // 发送邀请标记
  //           };
  //           form.setFieldValue("assessmentInviteItems", assessmentInviteItems);
  //         }
  //       } else {
  //         // 发送失败，也需要更新该标记为 false
  //         const assessmentInviteItems = form.getFieldValue(
  //           "assessmentInviteItems",
  //         );
  //         if (assessmentInviteItems && Array.isArray(assessmentInviteItems)) {
  //           assessmentInviteItems[index] = {
  //             ...assessmentInviteItems[index],
  //             isSendInvite, // 设为 false
  //           };
  //           form.setFieldValue("assessmentInviteItems", assessmentInviteItems);
  //         }
  //       }
  //     } finally {
  //       setSendingInvitationIndex(undefined);
  //     }
  //   },
  //   [handleSendInvitation, parentForm],
  // );

  // 处理删除邀请
  const handleDeleteInvitation = useCallback(async (id: string | number) => {
    await deleteEvaluationInvitation(id);
  }, []);

  // 不使用 useMemo，每次都重新生成以确保获取最新的表单值
  const formItems = getAssessmentInviteFormItems(
    handleDeleteRow,
    parentForm || formRef,
    evaluationCategoryOptions,
    undefined, // assessorOptions - 不再使用
    false, // assessorLoading - 不再使用
    undefined, // onAssessorSearch - 不再使用
    // handleSendInvitationClick, // 已注释：发送邀请功能已禁用
    undefined, // 发送邀请功能已禁用
    sendingInvitationIndex,
    handleOpenPersonnelModal,
    handleDeleteInvitation,
    editId, // 编辑接口的ID
    t, // 传递国际化函数
  );

  // 初始化表单数据
  React.useEffect(() => {
    const form = parentForm || formRef.current;
    if (form) {
      // 检查当前是否有 assessmentInviteItems 数据
      const currentData = form.getFieldValue("assessmentInviteItems");
      // 如果没有数据或数据为空，则初始化为默认数据
      if (
        !currentData ||
        (Array.isArray(currentData) && currentData.length === 0)
      ) {
        form.setFieldsValue({
          assessmentInviteItems: MOCK_ASSESSMENT_INVITE_DATA,
        });
      }
    } else {
    }
  }, [parentForm, t]);

  return (
    <div style={{ marginTop: "16px" }}>
      <DynamicForm
        ref={formRef}
        formItem={formItems}
        renderForm={false}
        formConfig={{
          onValuesChange: (changedValues: any, allValues: any) => {
            console.log("表单值变化:", allValues);
          },
        }}
      />
      {/* 人员选择弹窗 */}
      {(() => {
        // 动态导入 PersonnelSelectorModal，避免循环依赖
        const {
          PersonnelSelectorModal,
        } = require("../components/PersonnelSelectorModal");
        return (
          <PersonnelSelectorModal
            open={personnelModalOpen}
            onClose={handleClosePersonnelModal}
            onOk={handlePersonnelSelect}
          />
        );
      })()}
    </div>
  );
};

// ==================== 渲染函数 ====================
const renderTabContent = (
  key: string,
  backendData?: BackendEvaluationNode[],
  evaluationCategoryOptions: typeof EVALUATION_CATEGORIES = EVALUATION_CATEGORIES,
  editId?: string | number,
  t?: (key: string) => string,
) => {
  switch (key) {
    case "my-assessment":
      return <AssessmentTableContainer backendData={backendData} />;
    case "assessment-invite":
      return (
        <AssessmentInviteFormContainer
          evaluationCategoryOptions={evaluationCategoryOptions}
          editId={editId}
        />
      );
    default:
      return null;
  }
};

// ==================== Tab 渲染组件 ====================
/**
 * TabsField 组件
 * @param backendData 后端返回的评估分类树形数据
 * @param evaluationCategoryOptions 评估分类选项（用于评估邀请下拉框）
 *
 */
export const TabsField = React.forwardRef<
  {
    getEvaluationData: () => BackendEvaluationNode[];
    getRemarksData: () => Record<string, Record<string, string>>;
    clearAssessmentData: () => void;
    initializeEvaluationData: (data: BackendEvaluationNode[]) => void;
  },
  {
    backendData?: BackendEvaluationNode[];
    evaluationCategoryOptions?: typeof EVALUATION_CATEGORIES;
    editId?: string | number; // 编辑接口的ID
    isEdit?: boolean; // 是否为编辑模式
    currentPersonFlag?: boolean; // 用于控制"评估邀请"标签的显示隐藏（编辑模式下使用）
  }
>(
  (
    {
      backendData,
      evaluationCategoryOptions = EVALUATION_CATEGORIES,
      editId,
      isEdit = false,
      currentPersonFlag = false,
    },
    ref,
  ) => {
    const { t } = useTranslation("evaluationTab");
    const [activeKey, setActiveKey] = React.useState("my-assessment");
    const assessmentTableContainerRef = React.useRef<{
      getEvaluationData: () => BackendEvaluationNode[];
      getRemarksData: () => Record<string, Record<string, string>>;
      clearSelectionMap: () => void;
      initializeSelectionFromData: (data: BackendEvaluationNode[]) => void;
    }>(null);

    // 暴露获取评估数据的方法和清空方法
    React.useImperativeHandle(ref, () => ({
      getEvaluationData: () => {
        return assessmentTableContainerRef.current?.getEvaluationData() || [];
      },
      // 获取remarks数据
      getRemarksData: () => {
        return assessmentTableContainerRef.current?.getRemarksData() || {};
      },
      // 清空所有评估数据（包括表格选择和下拉选项）
      clearAssessmentData: () => {
        assessmentTableContainerRef.current?.clearSelectionMap();
      },
      // 初始化评估数据（用于编辑模式回显）
      initializeEvaluationData: (data: BackendEvaluationNode[]) => {
        assessmentTableContainerRef.current?.initializeSelectionFromData(data);
      },
    }));

    // 根据模式和 currentPersonFlag 过滤标签配置
    // 规则：新增模式下始终显示"评估邀请"标签，编辑模式下根据 currentPersonFlag 判断
    const filteredTabItems = TAB_ITEMS_CONFIG.filter((item) => {
      if (item.key === "assessment-invite") {
        // 新增模式下始终显示
        if (!isEdit) {
          return true;
        }
        // 编辑模式下根据 currentPersonFlag 判断
        return currentPersonFlag;
      }
      return true;
    });

    const items = filteredTabItems.map((item) => ({
      ...item,
      label: t(item.label),
      children:
        item.key === "my-assessment" ? (
          <AssessmentTableContainer
            ref={assessmentTableContainerRef}
            backendData={backendData}
          />
        ) : (
          renderTabContent(
            item.key,
            backendData,
            evaluationCategoryOptions,
            editId,
            t,
          )
        ),
    }));

    return (
      <div style={{ marginTop: "16px" }}>
        <Tabs
          type="card"
          activeKey={activeKey}
          onChange={setActiveKey}
          items={items}
        />
      </div>
    );
  },
);

TabsField.displayName = "TabsField";
